import { Button, Col, Row, Table, Pagination as Paging } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageHeader } from '../../../components/layout/PageHeader';
import { deleteFlow } from '../../../lib/api/flow-api';
import { isDocumentClassification, isMultiflow } from '../../../lib/util';
import { getAlgorithmFlowName } from '../../../lib/util/algorithm';
import { Pagination } from '../../../types';
import { Flow } from '../../../types/flow';
import CreateClassificationFlow from './CreateClassificationFlow';
import CreateFlow from './CreateFlow';
import { RenameFlow } from './RenameFlow';
import CreateMultiflow from './CreateMultiflow';
import { getClassificationTableColumns } from './FlowClassificationOverviewTable';
import { getTableColumns } from './FlowOverviewTable';
import { useGetAlgorithm, useGetFlowsForAlgorithm } from '../../../lib/hooks';
import { algorithmMenuDescriptions } from '../../../lib/status-templates';
import { ErrorText } from '../../../components/text/ErrorText';

const defaultPaging = {
  pageSize: 10,
  page: 1,
};

type Props = {};

const FlowOverview = (props: Props) => {
  const { algorithmId } = useParams() as { algorithmId: string };

  const [createFlow, setCreateFlow] = useState<boolean>(false);
  const [renameFlow, setRenameFlow] = useState<Flow | null>(null);
  const [paginationObject, setPaginationObject] = useState<Pagination>(defaultPaging);

  const { algorithm } = useGetAlgorithm(algorithmId);

  const { flows, totalItemCount, loading, refetch, error } = useGetFlowsForAlgorithm(algorithm?.id!, paginationObject);

  const onCreateDataset = () => {
    setCreateFlow(true);
  };

  const onClose = () => {
    setCreateFlow(false);
  };

  const onCloseRename = () => {
    setRenameFlow(null);
    refetch();
  };

  const onClickUrl = useCallback(
    (record: Flow) => {
      return `/flows/${algorithmId}/${record.id}`;
    },
    [algorithmId]
  );

  const onDelete = async (event: Event, flow: Flow) => {
    event.preventDefault();
    event.stopPropagation();
    await deleteFlow(flow.id);
    refetch();
  };

  const onRename = async (event: Event, flow: Flow) => {
    event.preventDefault();
    event.stopPropagation();
    setRenameFlow(flow);
  };

  const isClassification = algorithm && isDocumentClassification(algorithm);
  const isMultiflowAlgorithm = algorithm && isMultiflow(algorithm);

  useEffect(() => {
    setPaginationObject(defaultPaging);
  }, [algorithmId]);

  return (
    <>
      <PageHeader
        container="End to end"
        header={algorithm ? getAlgorithmFlowName(algorithm.name) || null : null}
        description={
          algorithm ? algorithmMenuDescriptions.flow[algorithm.type] || algorithmMenuDescriptions.flow.other : null
        }
      />
      <Row className="items-center my-3">
        <Col offset={18} span={6} className="flex justify-end">
          <Button type="primary" onClick={onCreateDataset} size={'large'}>
            New flow
          </Button>
        </Col>
      </Row>
      {algorithm && (
        <Row>
          <Col span={24}>
            {error ? (
              <ErrorText>Something went wrong. We could not find the flows you were looking for.</ErrorText>
            ) : (
              <>
                <Table
                  className="ant-table-toolbox"
                  columns={
                    isClassification
                      ? getClassificationTableColumns(onClickUrl, onDelete)
                      : getTableColumns(algorithm, onClickUrl, onDelete, onRename)
                  }
                  rowKey={record => record.id}
                  dataSource={flows}
                  pagination={false}
                  loading={loading}
                />
                <Row className="flex justify-end items-center my-4">
                  <Col className="mr-4">{totalItemCount} flows</Col>
                  <Paging
                    showSizeChanger={false}
                    defaultCurrent={1}
                    current={paginationObject.page}
                    pageSize={defaultPaging.pageSize}
                    total={totalItemCount}
                    onChange={page => {
                      setPaginationObject({ ...paginationObject, page: page });
                    }}
                  />
                </Row>
              </>
            )}
          </Col>
        </Row>
      )}
      {algorithm &&
        createFlow &&
        (isMultiflowAlgorithm ? (
          <CreateMultiflow visible={true} algorithm={algorithm} onClose={onClose} />
        ) : isClassification ? (
          <CreateClassificationFlow visible={true} algorithmId={algorithm.id} onClose={onClose} />
        ) : (
          <CreateFlow algorithm={algorithm} visible={true} onClose={onClose} />
        ))}
      {algorithm && renameFlow && (
        <RenameFlow
          visible={true}
          onClose={onCloseRename}
          onCancel={() => {
            setRenameFlow(null);
          }}
          flow={renameFlow}
        />
      )}
    </>
  );
};

export default FlowOverview;
